import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { QrCode, Camera, Check, X, RefreshCw, Calendar, Clock, User, Dumbbell } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function QRScanModule({ businessId, user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages", "employees", "qr"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [mode, setMode] = useState("generate"); // scan or generate
  const [qrCode, setQrCode] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [generatedQR, setGeneratedQR] = useState(null);
  const [qrDetails, setQrDetails] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const inputRef = useRef(null);

  // Fetch today's confirmed reservations
  useEffect(() => {
    if (businessId) {
      fetchReservations();
    }
  }, [businessId]);

  useEffect(() => {
    if (mode === "scan" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mode]);

  const fetchReservations = async () => {
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      // Fetch both confirmed and approved reservations
      const [confirmedRes, approvedRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/reservations?date=${today}&status=confirmed`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/reservations?date=${today}&status=approved`, { credentials: "include" })
      ]);
      
      let allData = [];
      if (confirmedRes.ok) {
        const data = await confirmedRes.json();
        allData = allData.concat(data);
      }
      if (approvedRes.ok) {
        const data = await approvedRes.json();
        allData = allData.concat(data);
      }
      
      // Filter only non-completed reservations
      const activeReservations = allData.filter(r => 
        ["confirmed", "approved"].includes(r.status) && !r.completed_at
      );
      setReservations(activeReservations);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    }
  };

  const handleGenerateQR = async () => {
    if (!selectedReservation) {
      toast.error(t("common:toast_lutfenBirRezervasyonSecin"));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API}/reservations/${selectedReservation}/generate-session-qr`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setGeneratedQR(data.session_qr);
        setQrDetails(data);
        setShowQRModal(true);
        toast.success(t("admin:toast_qrKodOlusturuldu"));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin:qrCreateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async (code) => {
    if (!code || loading) return;
    setLoading(true);
    setScanResult(null);

    try {
      const response = await fetch(`${API}/qr/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ qr_code: code }),
      });

      const data = await response.json();

      if (response.ok) {
        setScanResult({
          success: true,
          message: data.message || t('common:lessonCompleted'),
          details: data,
        });
        toast.success(t("common:toast_dersTamamlandi"));
        fetchReservations(); // Refresh list
      } else {
        setScanResult({
          success: false,
          message: data.detail || t("admin:lessonApproveFailed"),
        });
        toast.error(data.detail || t("admin:lessonApproveFailed"));
      }
    } catch (error) {
      setScanResult({
        success: false,
        message: t('common:errorOccurred'),
      });
      toast.error(t('common:errorOccurred'));
    } finally {
      setLoading(false);
      setQrCode("");
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQrCode(value);

    // Auto-submit when QR code is detected
    if (value.startsWith("SPOR-") && value.length >= 16) {
      handleScan(value);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleScan(qrCode);
    }
  };

  const getReservationInfo = (resId) => {
    return reservations.find(r => r.reservation_id === resId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t("admin:qrDersOnayi")}</h2>
          <p className="text-grey text-sm">{t("qr:rezervasyonIcinQrOlusturunVeya")}</p>
        </div>

        <div className="flex items-center bg-white rounded-xl border p-1">
          <Button
            variant={mode === "generate" ? "default" : "ghost"}
            size="sm"
            className={mode === "generate" ? "bg-navy" : ""}
            onClick={() => setMode("generate")}
            data-testid="qr-generate-tab"
          >
            <QrCode className="w-4 h-4 mr-2" />
            {t('qr:qrGenerate')}
          </Button>
          <Button
            variant={mode === "scan" ? "default" : "ghost"}
            size="sm"
            className={mode === "scan" ? "bg-navy" : ""}
            onClick={() => setMode("scan")}
            data-testid="qr-scan-tab"
          >
            <Camera className="w-4 h-4 mr-2" />
            {t('qr:qrScan')}
          </Button>
        </div>
      </div>

      {mode === "generate" ? (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">{t("qr:dersİcinQrOlustur")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Reservation Selection */}
            <div>
              <Label className="text-sm font-medium">{t("common:rezervasyonSecin")}</Label>
              <Select 
                value={selectedReservation || ""} 
                onValueChange={setSelectedReservation}
              >
                <SelectTrigger className="rounded-xl mt-2">
                  <SelectValue placeholder={t("common:ph_bugunkuRezervasyonlardanSecin")} />
                </SelectTrigger>
                <SelectContent>
                  {reservations.length === 0 ? (
                    <div className="p-4 text-center text-grey text-sm">
                      {t('admin:noConfirmedReservationToday')}
                    </div>
                  ) : (
                    reservations.map((res) => (
                      <SelectItem key={res.reservation_id} value={res.reservation_id}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-grey" />
                          <span className="font-medium">{res.start_time}</span>
                          <span className="text-grey">-</span>
                          <User className="w-4 h-4 text-grey" />
                          <span>{res.member_name || t('common:member')}</span>
                          {res.branch && (
                            <>
                              <span className="text-grey">•</span>
                              <span className="text-sm text-grey">{getLocalizedBranch(res.branch)}</span>
                            </>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Selected Reservation Info */}
            {selectedReservation && (
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                {(() => {
                  const res = getReservationInfo(selectedReservation);
                  if (!res) return null;
                  return (
                    <>
                      <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-navy" />
                        <span className="font-medium">{res.member_name || t('common:member')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-grey">
                        <Clock className="w-4 h-4" />
                        <span>{res.start_time} - {res.end_time}</span>
                      </div>
                      {res.trainer_name && (
                        <div className="flex items-center gap-2 text-sm text-grey">
                          <Dumbbell className="w-4 h-4" />
                          <span>{res.trainer_name}</span>
                        </div>
                      )}
                      {res.branch && (
                        <Badge variant="secondary" className="text-xs">
                          {getLocalizedBranch(res.branch)}
                        </Badge>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Generate Button */}
            <Button
              className="w-full bg-navy hover:bg-navy/90 rounded-xl h-12"
              onClick={handleGenerateQR}
              disabled={!selectedReservation || loading}
              data-testid="generate-qr-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <QrCode className="w-5 h-5 mr-2" />
                  {t('admin:qrCodeGenerate')}
                </>
              )}
            </Button>

            {/* Refresh */}
            <Button
              variant="outline"
              className="w-full rounded-xl"
              onClick={fetchReservations}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('common:listRefresh')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8">
            <div className="max-w-md mx-auto text-center">
              <div className="w-24 h-24 bg-navy/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Camera className="w-12 h-12 text-navy" />
              </div>

              <h3 className="font-heading font-semibold text-lg text-navy mb-2">
                {t('admin:qrCodeScan')}
              </h3>
              <p className="text-grey mb-6">
                {t('admin:qrCodeScanDesc')}
              </p>

              <Input
                ref={inputRef}
                placeholder={t("admin:ph_qrKodOtomatikAlgilanacak")}
                value={qrCode}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                className="text-center h-14 text-lg rounded-xl mb-4"
                data-testid="qr-input"
              />

              <Button
                className="bg-navy hover:bg-navy/90 rounded-xl px-8"
                onClick={() => handleScan(qrCode)}
                disabled={!qrCode || loading}
                data-testid="scan-qr-btn"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {t('common:completeLesson')}
                  </>
                )}
              </Button>

              {/* Scan Result */}
              {scanResult && (
                <div
                  className={`mt-6 p-4 rounded-xl ${
                    scanResult.success
                      ? "bg-green-50 border border-green-200"
                      : "bg-red-50 border border-red-200"
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    {scanResult.success ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <X className="w-5 h-5 text-red-600" />
                    )}
                    <span
                      className={
                        scanResult.success ? "text-green-700" : "text-red-700"
                      }
                    >
                      {scanResult.message}
                    </span>
                  </div>
                  {scanResult.success && scanResult.details?.remaining_sessions !== null && (
                    <p className="text-sm text-green-600 mt-2">
                      {t('packages:remainingLesson')}: {scanResult.details.remaining_sessions}
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="border-0 shadow-sm bg-blue-50">
        <CardContent className="p-6">
          <h4 className="font-semibold text-blue-900 mb-3">{t('common:howItWorks')}</h4>
          <ol className="space-y-2 text-blue-800 text-sm">
            <li className="flex items-start gap-2">
              <span className="font-bold">1.</span>
              <span>{t('qr:qrStep1')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">2.</span>
              <span>{t('admin:qrStep2')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">3.</span>
              <span>{t("admin:uyeTelefonuylaQrKoduOkuttur")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">4.</span>
              <span>{t("packages:dersOtomatikTamamlanirVePaketten")}</span>
            </li>
            <li className="flex items-start gap-2 text-blue-600">
              <span className="font-bold">*</span>
              <span>{t("employees:uyeQrOkutmazsaYoneticiManuel")}</span>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* QR Display Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">{t('admin:lessonQrCode')}</DialogTitle>
          </DialogHeader>
          
          {qrDetails && (
            <div className="text-center space-y-6 py-4">
              {/* QR Code */}
              <div className="bg-white p-6 rounded-2xl shadow-lg inline-block mx-auto">
                <QRCodeSVG
                  value={generatedQR || ""}
                  size={220}
                  level="H"
                  includeMargin
                />
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center gap-2 text-navy font-medium">
                  <User className="w-4 h-4" />
                  {qrDetails.member_name}
                </div>
                <div className="flex items-center justify-center gap-2 text-grey">
                  <Calendar className="w-4 h-4" />
                  {qrDetails.date && format(new Date(qrDetails.date), "d MMMM yyyy", { locale: getDateLocale() })}
                </div>
                <div className="flex items-center justify-center gap-2 text-grey">
                  <Clock className="w-4 h-4" />
                  {qrDetails.start_time}
                </div>
                {qrDetails.branch && (
                  <Badge variant="secondary" className="mx-auto">
                    {getLocalizedBranch(qrDetails.branch)}
                  </Badge>
                )}
              </div>

              {/* Instructions */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                <p className="font-medium mb-1">{t("common:uyeyeGosterin")}</p>
                <p>{t("admin:uyeBuQrKoduTelefonuyla")}</p>
              </div>

              {/* Expiry */}
              <p className="text-xs text-grey">
                {t('admin:qrExpiry')}
              </p>

              {/* Close Button */}
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => setShowQRModal(false)}
              >
                {t('common:close')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
